import styled from 'styled-components';
import RDatePicker from 'react-datepicker';
import { space, width, height, fontSize, minHeight, maxHeight, fontWeight, borderRadius } from 'styled-system';
// helpers/constants
import * as G from '../../../helpers';
// ui
import { AbsoluteBox } from '../../../ui';
//////////////////////////////////////////////////

const renderBorderColor = ({ hasError }) => {
  if (hasError) {
    return G.getTheme('forms.inputs.borderColorErr');
  }

  return G.getTheme('colors.dark.grey');
};

const renderInputHeight = (value) => {
  if (G.isNumber(value)) return `${value}px`;

  return value || '36px';
};

const renderSelectArrowTop = ({ afterTop, height }) => {
  if (G.isNotNilAndNotEmpty(afterTop)) return afterTop;

  if (G.isNumber(height)) return `${(height - 6) / 2}px`;

  return `calc(${renderInputHeight(height)} / 2 - 3px)`;
};

const LabelBox = styled(AbsoluteBox)`
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  & .required::after {
    content: '*';
    color: ${() => G.getTheme('colors.light.mainRed')};
  }
`;

const Error = styled.span`
  ${width}
  ${space}

  font-size: 11px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  text-overflow: ellipsis;
  left: ${({ left }) => left || '5px'};
  width: ${({ width }) => width || '100%'};
  z-index: ${({ zIndex }) => zIndex || 'unset'};
  top: ${({ top }) => top || 'calc(100% + 1px)'};
  color: ${() => G.getTheme('forms.inputs.errorTextColor')};
`;

Error.defaultProps = {
  width: 'max-content',
};

const Input = styled.input`
  ${space}
  ${width}
  ${height}
  ${fontSize}
  ${fontWeight}
  ${borderRadius}

  cursor: text;
  outline: none;
  line-height: 36px;
  border: 1px solid ${(props) => renderBorderColor(props)};
  background-color: ${({ disabled }) => (
    G.getTheme(G.ifElse(disabled, 'forms.inputs.bgDisabled', 'forms.inputs.bgColor'))
  )};

  &:focus {
    box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
  }

  &[type=number]::-webkit-inner-spin-button,
  &[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    margin: 0;
  }

  &[type=color] {
    padding: 0;
    border: none;
    cursor: pointer;

    &::-webkit-color-swatch-wrapper { padding: 0 }

    &::-webkit-color-swatch {
      border: none;
    }

    &::-moz-color-swatch {
      border: none;
    }

    &:focus {
      box-shadow: none;
    }
  }
`;

Input.defaultProps = {
  pl: '8px',
  pr: '8px',
  height: 36,
  borderRadius: 4,
};

const MultiValuesInput = styled.input`
  ${space}
  ${width}
  ${height}

  border: none;
  outline: none;

  display: ${({ display }) => display};
  min-width: ${({ minWidth }) => minWidth};
  background: ${({ background }) => background};

  &:focus {
    border: none;
    box-shadow: none;
  }
`;

const Textarea = styled.textarea`
  ${space}
  ${width}
  ${height}
  ${fontSize}
  ${minHeight}
  ${maxHeight}
  ${borderRadius}

  cursor: text;
  outline: none;
  resize: ${({ resize }) => resize};
  line-height: ${({ lineHeight }) => lineHeight};
  border: 1px solid ${(props) => renderBorderColor(props)};
  background-color: ${({ disabled }) => (
    G.getTheme(G.ifElse(disabled, 'forms.inputs.bgDisabled', 'forms.inputs.bgColor'))
  )};

  &:focus {
    box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
  }
`;

Textarea.defaultProps = {
  height: 80,
  resize: 'none',
  borderRadius: 4,
  lineHeight: '20px',
  padding: '2px 8px',
};

const SelectWrapper = styled.div`
  ${width}

  &:after {
    width: 6px;
    height: 6px;
    content: '';
    border: solid;
    position: absolute;
    pointer-events: none;
    border-width: 0px 2px 2px 0;
    transition: border-color 0.4s;
    transform: rotate(45deg) translate(0, -60%);
    border-color: ${G.getTheme('colors.dark.grey')};
    top: ${renderSelectArrowTop};
    right: ${({ afterRight }) => afterRight || '12px'};
  }

  &:hover:after {
    border-color: ${G.getTheme('colors.light.darksGrey')};
  }
`;

const InputSelect = styled.select`
  ${space}
  ${width}
  ${height}
  ${borderRadius}

  outline: none;
  appearance: none;
  position: relative;
  padding: 0 20px 0 8px;
  line-height: ${({ height }) => renderInputHeight(height)};
  background-color: ${() => G.getTheme('forms.inputs.bgColor')};
  border: 1px solid ${(props) => renderBorderColor(props)};
  background-color: ${({ disabled }) => (
    G.getThemeByCond(disabled, 'forms.inputs.bgDisabled', 'forms.inputs.bgColor')
  )};

  &:focus {
    box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
  }
`;

InputSelect.defaultProps = {
  height: 36,
  borderRadius: 4,
};

const Checkbox = styled.input`
  ${space}

  position: relative;

  &:focus {
    outline: none;
  }
  &:before {
    border: 1px solid ${() => G.getTheme('forms.checkboxes2.borderColor')};
    left: 0;
    top: 50%;
    content: '';
    width: 17px;
    height: 17px;
    border-radius: 3px;
    position: absolute;
    transform: translate(0, -50%);
    background-color: ${() => G.getTheme('forms.checkboxes2.bgColor')};
  }
  &:checked:before {
    border: 1px solid ${() => G.getTheme('forms.checkboxes2.borderColor')};
    content: '';
    width: 17px;
    height: 17px;
    border-radius: 3px;
    position: absolute;
    background-color: ${() => G.getTheme('forms.checkboxes2.bgColor')};
  }
  &:checked:after {
    border: solid ${() => G.getTheme('forms.checkboxes2.textColor')};
    top: 50%;
    left: 1px;
    width: 3px;
    height: 6px;
    content: '';
    position: absolute;
    border-width: 0px 3px 3px 0;
    transform: rotate(45deg) translate(0, -90%);
  }
  &:disabled {
    cursor: not-allowed;
  }
  &:disabled:before,
  &:disabled:checked:before {
    border: 1px solid ${() => G.getTheme('colors.light.darkGrey')};
    background-color: ${() => G.getTheme('colors.lightGrey')};
  }
  &:disabled:checked:after {
    border-color: ${() => G.getTheme('colors.darkGrey')};
  }
`;

const CountrySelectWrapper = styled.div`
  ${space}
  ${width}
  ${height}

  position: relative;

  & > select {
    z-index: 0;
    width: 100%;
    outline: none;
    appearance: none;
    line-height: 36px;
    padding-left: 8px;
    border-radius: 4px;
    position: relative;
    padding-right: 20px;
    height: ${({ height }) => renderInputHeight(height)};
    border: 1px solid ${(props) => renderBorderColor(props)};
    background-color: ${() => G.getTheme('forms.inputs.bgColor')};

    &:focus {
      box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
    }
  }

  &:after {
    top: 15px;
    width: 6px;
    content: '';
    right: 12px;
    height: 6px;
    border: solid;
    position: absolute;
    pointer-events: none;
    border-width: 0px 2px 2px 0;
    transition: border-color 0.4s;
    transform: rotate(45deg) translate(0, -60%);
    border-color: ${G.getTheme('colors.dark.grey')};
  }

  &:hover:after {
    border-color: ${G.getTheme('colors.light.darksGrey')};
  }
`;

const DatePicker = styled(RDatePicker)`
  ${space}
  ${width}

  height: 36px;
  cursor: text;
  outline: none;
  line-height: 36px;
  padding-left: 8px;
  border-radius: 4px;
  background-color: ${() => G.getTheme('inputs.bgColor')};
  border: 1px solid ${(props) => renderBorderColor(props)};

  &:focus {
    box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
  }
`;

const CalendarWrapper = styled.div`
  ${space}
  ${width}

  & .react-datepicker-popper[data-placement^="top"] {
    margin-left: 10px;
  }
  & .react-datepicker-popper[data-placement^="bottom"] {
    margin-top: 0;
  }
  & .react-datepicker__triangle {
    display: none;
  }
  & .react-datepicker__navigation--previous {
    border-right-color: ${() => G.getTheme('colors.light.mainRed')};
  }
  & .react-datepicker__navigation--next {
    border-left-color: ${() => G.getTheme('colors.light.mainRed')};
  }
  & .react-datepicker__day--selected {
    background-color: ${() => G.getTheme('colors.light.mainRed')};
    &:hover {
      background-color: ${() => G.getTheme('colors.light.mainRed')};
    }
  }
  & .react-datepicker {
    box-shadow: 0 0 1px 1px ${() => G.getTheme('colors.light.mainRed')};
  }
  & .react-datepicker__header--time {
    padding-top: 18px;
    padding-bottom: 18px;
  }
  & .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list {
      padding: 0;
      height: 183px !important;
      & li.react-datepicker__time-list-item {
        padding: 5px 6px;
      }
      & li.react-datepicker__time-list-item--selected {
        background-color: ${() => G.getTheme('colors.light.mainRed')};
      &:hover {
        background-color: ${() => G.getTheme('colors.light.mainRed')};
        }
      }
    }
  & .react-datepicker__close-icon {
    right: 15px;
  }
  & .react-datepicker-popper {
    z-index: 1000;
  }
`;

const CalendarIconWrapper = styled(CalendarWrapper)`
  & .react-datepicker-popper[data-placement^="bottom-start"] {
    transform: translate3d(0px, 38px, 0px) !important;
  }
  & .react-datepicker-popper[data-placement^="top-start"] {
    transform: translate3d(-10px, -101%, 0px) !important;
  }
`;

const PhoneNumberInputWrapper = styled.div`
  ${space}
  ${width}
  ${height}

  & .PhoneInput > input {
    border: none;
    outline: none;
    border-bottom: 1px solid;
    width: calc(100% - 30px);
    height: ${({ height }) => renderInputHeight(height)};

    border-color: ${(props) => renderBorderColor(props)};

    &:focus {
      border-color: rgba(206, 40, 40, 0.5);
    }
  }
`;

export {
  Input,
  Error,
  Textarea,
  Checkbox,
  LabelBox,
  DatePicker,
  InputSelect,
  SelectWrapper,
  CalendarWrapper,
  MultiValuesInput,
  renderBorderColor,
  CalendarIconWrapper,
  CountrySelectWrapper,
  PhoneNumberInputWrapper,
};
